import {useEffect, useRef, useState} from 'react';
import applyToAll from '../applyToAll';
import mergeRefs from '../mergeRefs';
import usePopper from '../usePopper';

const DEFAULT_OPTIONS = {
  modifiers: [],
  placement: 'bottom',
};

const TOOLTIP_EVENT_STATES = {
  HIDE: 0,
  SHOW: 1,
};

/**
 * To handle the accessibility issue of only ever displaying one tooltip on the
 * screen, we're using a global subscription system to update tooltip
 * components without them knowing about each other. We're returning an object
 * with two methods to either subscribe to or emit events.
 */
const createSubscriber = () => {
  // Every tooltip has a unique id which we use a counter for
  let runningId = 0;
  // The id of the last shown tooltip
  let lastActiveId = null;
  // Every subscriber is an array of [id, callback]
  const subscribers = [];

  return {
    /**
     * Creates an id using our accumulator to add to our subscribers with its
     * callback.
     */
    subscribe(callback) {
      const id = runningId++;
      subscribers.push([id, callback]);
      return id;
    },
    // Emits an event that's passed to all our subscribers.
    emit(emitId, event) {
      /**
       * If a show event, we find the subscriber that matches to our
       * lastActiveId in order to emit a HIDE event to that tooltip. This way we
       * ensure there's only ever one tooltip on screen.
       */
      if (event === TOOLTIP_EVENT_STATES.SHOW) {
        subscribers.forEach(([id, callback]) => {
          if (id === lastActiveId && id !== emitId) {
            callback(TOOLTIP_EVENT_STATES.HIDE);
          }
        });
        // Set to new id
        lastActiveId = emitId;
      }
    },
  };
};

const tooltipSubscriber = createSubscriber();

export default function useTooltip(options = DEFAULT_OPTIONS) {
  // Using state below to make sure popper is always run after our refs are
  // passed back. If we use regular refs, there's no guarantee that popper will
  // update correctly with the elements they're pointing to.

  // Ref for the target element that will trigger the tooltip
  const [referenceRef, setReferenceRef] = useState();
  // Ref for the tooltip
  const [popperRef, setPopperRef] = useState();
  // Using a ref because this should never change.
  const id = useRef();

  const popper = usePopper({
    modifiers: options.modifiers,
    placement: options.placement,
    strategy: options.strategy,
    popperNode: popperRef,
    targetNode: referenceRef,
  });

  const [isVisible, setIsVisible] = useState(false);
  const [state, setState] = useState(TOOLTIP_EVENT_STATES.HIDE);

  const handleShow = () => {
    setState(TOOLTIP_EVENT_STATES.SHOW);
    tooltipSubscriber.emit(id.current, TOOLTIP_EVENT_STATES.SHOW);
  };
  const handleHide = () => {
    setState(TOOLTIP_EVENT_STATES.HIDE);
    tooltipSubscriber.emit(id.current, TOOLTIP_EVENT_STATES.HIDE);
  };

  // Sets up our subscriber to hide the tooltip on HIDE events.
  useEffect(() => {
    // Ensures we don't call our subscribed state setter on an unmounted
    // component.
    let mounted = true;

    id.current = tooltipSubscriber.subscribe((event) => {
      if (event === TOOLTIP_EVENT_STATES.HIDE && mounted) {
        setIsVisible(false);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  // Changes the visibility based on state.
  useEffect(() => {
    // Slightly longer delay for HIDE events for readibility.
    const delay = state === TOOLTIP_EVENT_STATES.SHOW ? 100 : 300;
    // Store timeout to clear it if the component unmounts before it completes
    const timer = setTimeout(() => {
      tooltipSubscriber.emit(id.current, state);
      setIsVisible(state === TOOLTIP_EVENT_STATES.SHOW);
    }, delay);

    return () => clearTimeout(timer);
  }, [state]);

  /**
   * The id is used to link the tooltip to its trigger for accessibility
   * reasons. We only need the id when the tooltip is visible.
   */
  const tooltipId = isVisible ? `tooltip--${id.current}` : null;

  return {
    isVisible,
    getArrowProps: (props = {}) => ({
      ...props,
      ...popper.arrowProps,
      ref: mergeRefs(popper.arrowProps.ref, props?.ref),
      style: {
        ...props?.style,
        ...popper.arrowProps.style,
      },
    }),
    getTooltipProps: (props = {}) => ({
      ...props,
      ...popper.popperProps,
      // Links the tooltip to the trigger for a11y support
      id: tooltipId,
      ref: mergeRefs(setPopperRef, props?.ref),
      style: {
        ...props?.style,
        ...popper.popperProps.style,
      },
    }),
    getTriggerProps: (props = {}) => ({
      ...props,
      // Links the trigger to the tooltip for a11y support
      'aria-describedby': tooltipId,
      // Ensure we don't overwrite event handlers attached to the trigger
      onBlur: applyToAll(handleHide, props?.onBlur),
      onFocus: applyToAll(handleShow, props?.onFocus),
      onMouseEnter: applyToAll(handleShow, props?.onMouseEnter),
      onMouseLeave: applyToAll(handleHide, props?.onMouseLeave),
      ref: mergeRefs(setReferenceRef, props?.ref),
    }),
  };
}
